import { useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useModal } from '@app/context/ModalsContext'
import { useSnackbar } from '@app/context/SnackbarContext'

import { useNavigate } from '@lib/react-router-dom'

import { CreateLayoutModal } from '@pages/IntroducingBroker/DashboardPoC/components/CreateLayout.modal'
import { EditLayoutMetadataModal } from '@pages/IntroducingBroker/DashboardPoC/components/EditLayoutMetadata.modal'
import { LayoutActionConfirmModal } from '@pages/IntroducingBroker/DashboardPoC/components/LayoutActionConfirmModal/LayoutActionConfirm.modal'
import { useDashboardVisibility } from '@pages/IntroducingBroker/DashboardPoC/hooks/useDashboardVisibility'
import { useResolvedLayout } from '@pages/IntroducingBroker/DashboardPoC/hooks/useResolvedLayout'
import { useVisibleWidgets } from '@pages/IntroducingBroker/DashboardPoC/hooks/useVisibleWidgets'
import { getPersistedLayoutItem } from '@pages/IntroducingBroker/DashboardPoC/utils/dashboardGrid.utils'
import {
  buildPresetWidgets,
  buildSelectedWidgets,
} from '@pages/IntroducingBroker/DashboardPoC/utils/dashboardWidgetSelection.utils'
import { getSystemLayout } from '@pages/IntroducingBroker/DashboardPoC/utils/getSystemLayout'
import { widgetRegistry } from '@pages/IntroducingBroker/DashboardPoC/widgetRegistry'
import { useLoyaltyData } from '@pages/IntroducingBroker/IntroducingBroker'

import { useWindowResize } from '@utils/domUtils'

import type { LoyaltyDashboardDetailsDto } from '@model/IntroducingBrokerLoyalty'

import { Loading } from '@shared/components/Loading/Loading'

import { WidgetPalette } from './components/WidgetPalette.modal'
import type { DashboardGridChange, DashboardLayout, LocalEdits, TempLayout, Widget } from './types'
import { MAX_CUSTOM_LAYOUTS, WidgetType } from './types'
import { useDashboard } from './useDashboard'
import { arrangeMobileWidgets, getWidgetConstraints } from './widgetLayouts'

export interface DashboardContextValue {
  isLoading: boolean
  error: Error | null
  isMobile: boolean
  currentLayoutId: string
  currentLayout: DashboardLayout | null
  visibleWidgets: Widget[]
  canAddMoreLayouts: boolean
  handleLayoutChange: (change: DashboardGridChange) => void
  handleRemoveWidgetByType: (widgetType: WidgetType) => void
  handleOpenWidgetPalette: () => void
  handleCreateLayout: (
    name: string,
    description: string,
    presetId?: string,
    isDefault?: boolean
  ) => void
  handleEditLayoutMetadata: (name: string, description: string) => void
  handleLayoutSelect: (layoutId: string) => void
  handleEnterEditMode: (layoutId: string) => void
  handleSave: () => Promise<void>
  handleSetDefaultLayout: (layoutId: string, isDefault?: boolean) => Promise<void>
  handleDeleteLayout: (layoutId: string) => Promise<void>
  handleEditsCancel: () => void
  layoutCRUDActionModal: ReturnType<typeof useModal<typeof LayoutActionConfirmModal>>
  createLayoutModal: ReturnType<typeof useModal<typeof CreateLayoutModal>>
  editLayoutModal: ReturnType<typeof useModal<typeof EditLayoutMetadataModal>>
  layouts: DashboardLayout[]
  defaultLayoutId: string
  ibLoyaltyData?: LoyaltyDashboardDetailsDto | null
  isLoyaltyDataLoading?: boolean
  refreshIbLoyaltyData?: (skipLoadingFlag?: boolean) => void
}

export const DashboardWrapper = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { layoutId } = useParams<{ layoutId?: string }>()
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()

  const isMobile = useWindowResize()
  const visibility = useDashboardVisibility()
  const { ibLoyaltyData, isLoyaltyDataLoading, refreshIbLoyaltyData } = useLoyaltyData()
  const { layouts, availableWidgets, isLoading, error, createLayout, updateLayout, deleteLayout } =
    useDashboard()

  const [tempLayout, setTempLayout] = useState<TempLayout | null>(null)
  const [localEdits, setLocalEdits] = useState<LocalEdits | null>(null)
  const [widgetSelectionPreview, setWidgetSelectionPreview] = useState<WidgetType[] | null>(null)
  const [currentLayoutId, setCurrentLayoutId] = useState('')
  const [isPending, startTransition] = useTransition()

  const layoutCRUDActionModal = useModal(LayoutActionConfirmModal)
  const createLayoutModal = useModal(CreateLayoutModal)
  const editLayoutModal = useModal(EditLayoutMetadataModal)
  const widgetPaletteModal = useModal(WidgetPalette)

  const dashboardPath = location.pathname.replace(/\/edit\/[^/]+\/?$/, '')
  const systemLayoutId = getSystemLayout(layouts)?.id ?? ''
  const defaultLayoutId = layouts.find((layout) => layout.isDefault)?.id ?? systemLayoutId

  useEffect(() => {
    if (!layoutId) {
      setTempLayout(null)
      setLocalEdits(null)
      setWidgetSelectionPreview(null)
    }
  }, [layoutId])

  useEffect(() => {
    if (layoutId) {
      if (isLoading) return

      const layout = layouts.find(({ id }) => id === layoutId)
      const isValidTempLayout = layoutId === 'temp' && tempLayout !== null

      if (!isValidTempLayout && (!layout || layout.isPreset)) {
        navigate(dashboardPath, { replace: true })
        return
      }

      setCurrentLayoutId(layoutId)
      return
    }

    if (layouts.length === 0) {
      return
    }

    const lastVisitedLayoutId = location.state?.lastVisitedLayoutId
    const lastVisitedLayoutExists = layouts.some(({ id }) => id === lastVisitedLayoutId)
    const currentLayoutExists = layouts.some(({ id }) => id === currentLayoutId)
    const nextLayoutId =
      (lastVisitedLayoutExists ? lastVisitedLayoutId : undefined) ??
      (currentLayoutExists ? currentLayoutId : undefined) ??
      defaultLayoutId

    if (nextLayoutId !== currentLayoutId) {
      setCurrentLayoutId(nextLayoutId)
      return
    }

    if (lastVisitedLayoutId) navigate(location.pathname, { replace: true, state: null })
  }, [
    currentLayoutId,
    dashboardPath,
    defaultLayoutId,
    isLoading,
    layoutId,
    layouts,
    location.pathname,
    location.state,
    navigate,
    tempLayout,
  ])

  const currentLayout = useResolvedLayout(layouts, currentLayoutId, tempLayout, localEdits)
  const visibleAvailableWidgets = useMemo(
    () => availableWidgets.filter((widget) => widgetRegistry[widget.type].isVisible(visibility)),
    [availableWidgets, visibility]
  )
  const previewWidgets = useMemo(() => {
    if (widgetSelectionPreview === null || !currentLayout || currentLayout.isPreset) {
      return currentLayout?.widgets ?? []
    }

    return buildSelectedWidgets(
      currentLayout.widgets,
      visibleAvailableWidgets,
      widgetSelectionPreview
    )
  }, [currentLayout, visibleAvailableWidgets, widgetSelectionPreview])
  const visibleWidgets = useVisibleWidgets(previewWidgets, visibility, isMobile)

  const customLayoutCount = layouts.filter((layout) => !layout.isPreset).length
  const canAddMoreLayouts = customLayoutCount < MAX_CUSTOM_LAYOUTS

  const updateEditedWidgets = useCallback(
    (widgets: Widget[]) => {
      if (!currentLayout || currentLayout.isPreset) return

      const arrangedWidgets = arrangeMobileWidgets(widgets)

      if (tempLayout) {
        setTempLayout((current) => (current ? { ...current, widgets: arrangedWidgets } : current))
        return
      }

      setLocalEdits((current) => ({
        layoutId: currentLayoutId,
        widgets: arrangedWidgets,
        name: current?.layoutId === currentLayoutId ? current.name : undefined,
        description: current?.layoutId === currentLayoutId ? current.description : undefined,
      }))
    },
    [currentLayout, currentLayoutId, tempLayout]
  )

  const handleLayoutChange = useCallback(
    ({ layout: desktopLayouts, changedItem, interaction }: DashboardGridChange) => {
      if (!currentLayout || currentLayout.isPreset) {
        return
      }

      const widgets = currentLayout.widgets.map((widget) => {
        const nextLayout = desktopLayouts.find(({ i }) => i === widget.id)

        if (!nextLayout) return widget

        const persistedLayout = getPersistedLayoutItem(nextLayout, widget.layout, {
          changedItem,
          interaction,
        })

        return {
          ...widget,
          layout: getWidgetConstraints(persistedLayout, widget.id, widget.layout, widget.size),
        }
      })

      updateEditedWidgets(widgets)
    },
    [currentLayout, updateEditedWidgets]
  )

  const handleRemoveWidgetByType = useCallback(
    (widgetType: WidgetType) => {
      if (!currentLayout || currentLayout.isPreset) {
        return
      }

      updateEditedWidgets(currentLayout.widgets.filter(({ type }) => type !== widgetType))
    },
    [currentLayout, updateEditedWidgets]
  )

  const handleOpenWidgetPalette = useCallback(() => {
    if (!currentLayout || currentLayout.isPreset) return

    widgetPaletteModal.open({
      existingWidgets: currentLayout.widgets,
      availableWidgets: visibleAvailableWidgets,
      onChange: setWidgetSelectionPreview,
      onSave: (widgetTypes) => {
        updateEditedWidgets(
          buildSelectedWidgets(currentLayout.widgets, visibleAvailableWidgets, widgetTypes)
        )
        setWidgetSelectionPreview(null)
      },
      onClose: () => setWidgetSelectionPreview(null),
    })
  }, [currentLayout, updateEditedWidgets, visibleAvailableWidgets, widgetPaletteModal])

  const handleCreateLayout = (
    name: string,
    description: string,
    presetId?: string,
    isDefault?: boolean
  ) => {
    if (!canAddMoreLayouts) {
      addSnackbar.error({
        message: t('Dashboard.MaxCustomLayoutsReached', { count: MAX_CUSTOM_LAYOUTS }),
      })
      return
    }

    const baseWidgets = presetId
      ? buildPresetWidgets(
          layouts.find((layout) => layout.id === presetId && layout.isPreset)?.widgets ?? [],
          visibleAvailableWidgets
        )
      : []

    setTempLayout({
      name,
      description: description || t('Dashboard.DefaultCustomLayoutDescription'),
      widgets: baseWidgets,
      isDefault,
    })
    setCurrentLayoutId('temp')
    startTransition(() => navigate('edit/temp'))
  }

  const handleEditLayoutMetadata = (name: string, description: string) => {
    if (tempLayout) {
      setTempLayout((current) => (current ? { ...current, name, description } : current))
      return
    }

    if (!currentLayout || currentLayout.isPreset) return

    setLocalEdits((current) => ({
      layoutId: currentLayoutId,
      widgets: current?.layoutId === currentLayoutId ? current.widgets : currentLayout.widgets,
      name,
      description,
    }))
  }

  const handleLayoutSelect = (selectedLayoutId: string) => {
    setCurrentLayoutId(selectedLayoutId)
  }

  const handleEnterEditMode = (selectedLayoutId: string) => {
    const layout = layouts.find(({ id }) => id === selectedLayoutId)

    if (!layout || layout.isPreset) return

    setCurrentLayoutId(selectedLayoutId)
    startTransition(() => navigate('edit/' + selectedLayoutId))
  }

  const handleSetDefaultLayout = async (selectedLayoutId: string, isDefault = true) => {
    const layout = layouts.find(({ id }) => id === selectedLayoutId)

    if (!layout) {
      return
    }

    try {
      await updateLayout(
        selectedLayoutId,
        layout.name,
        layout.description,
        layout.widgets,
        isDefault
      )
      addSnackbar.success({ message: t('Dashboard.Custom layout updated successfully') })
    } catch (error) {
      console.error('Failed to update the default dashboard layout:', error)
      addSnackbar.error({ message: t('Dashboard.SaveLayoutError') })
    }
  }

  const handleSave = async () => {
    try {
      let lastVisitedLayoutId = currentLayoutId

      if (tempLayout) {
        lastVisitedLayoutId = await createLayout(
          tempLayout.name,
          tempLayout.description,
          tempLayout.widgets,
          tempLayout.isDefault
        )
        setCurrentLayoutId(lastVisitedLayoutId)
        addSnackbar.success({ message: t('Dashboard.Custom layout created successfully') })
      } else if (localEdits && currentLayout) {
        await updateLayout(
          localEdits.layoutId,
          localEdits.name ?? currentLayout.name,
          localEdits.description ?? currentLayout.description,
          localEdits.widgets
        )
        addSnackbar.success({ message: t('Dashboard.Custom layout updated successfully') })
      }

      navigate(dashboardPath, { state: { lastVisitedLayoutId } })
    } catch (error) {
      console.error('Failed to save the dashboard layout:', error)
      addSnackbar.error({ message: t('Dashboard.SaveLayoutError') })
    }
  }

  const handleDeleteLayout = async (selectedLayoutId: string) => {
    const layout = layouts.find(({ id }) => id === selectedLayoutId)

    if (!layout || layout.isPreset) return

    try {
      await deleteLayout(selectedLayoutId)

      if (currentLayoutId === selectedLayoutId) {
        const remainingLayouts = layouts.filter(({ id }) => id !== selectedLayoutId)
        const nextLayout =
          remainingLayouts.find(({ isDefault }) => isDefault) ?? getSystemLayout(remainingLayouts)

        setCurrentLayoutId(nextLayout?.id ?? '')
      }

      addSnackbar.success({ message: t('Dashboard.Custom layout deleted successfully') })
    } catch (error) {
      console.error('Failed to delete the dashboard layout:', error)
      addSnackbar.error({ message: t('Dashboard.DeleteLayoutError') })
    }
  }

  const handleEditsCancel = useCallback(() => {
    navigate(dashboardPath, { state: { lastVisitedLayoutId: currentLayoutId } })
  }, [currentLayoutId, dashboardPath, navigate])

  const value: DashboardContextValue = {
    isLoading: isLoading || !visibility.isVisibilityReady,
    error,
    isMobile,
    currentLayoutId,
    currentLayout,
    visibleWidgets,
    canAddMoreLayouts,
    handleLayoutChange,
    handleRemoveWidgetByType,
    handleOpenWidgetPalette,
    handleCreateLayout,
    handleEditLayoutMetadata,
    handleLayoutSelect,
    handleEnterEditMode,
    handleSave,
    handleSetDefaultLayout,
    handleDeleteLayout,
    handleEditsCancel,
    layoutCRUDActionModal,
    createLayoutModal,
    editLayoutModal,
    layouts,
    defaultLayoutId,
    ibLoyaltyData,
    isLoyaltyDataLoading,
    refreshIbLoyaltyData,
  }

  return isPending ? <Loading isLoading showLoadingIcon /> : <Outlet context={value} />
}
